import { FC, useMemo, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";

import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import SearchBar from "@/components/SearchBar";
import { FullWidthRow, ResponsiveTable, Table, Th } from "@/components/Table";
import Text from "@/components/Text";
import AddButton from "@/components/AddButton";
import LinkFormModal from "@/pages/LinksLibrary/components/LinkFormModal";
import LinksTableRow from "@/pages/LinksLibrary/components/LinksTableRow";
import { LibraryLinkResource } from "@/types/resources";
import useDebounce from "@/hooks/useDebounce";

const StyledTable = styled(Table)`
  position: relative;
  th {
    vertical-align: top;
  }
  td,
  th {
    &:nth-child(1) {
      width: 54%;
      padding-right: 40px;
    }
    &:nth-child(2) {
      width: 39%;
      padding-left: 40px;
    }
    &:nth-child(3) {
      width: 7%;
    }
  }
`;

interface Props {
  heading: string;
  type: "tool" | "report";
  links?: LibraryLinkResource[];
}

const LinksSection: FC<Props> = ({ heading, type, links }) => {
  const [search, setSearch] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);

  const debouncedSearch = useDebounce(search, 350);

  const filteredLinks = useMemo(() => {
    if (!debouncedSearch) return links;

    const s = debouncedSearch.toLowerCase();

    return links?.filter(
      (link) =>
        link.name.toLowerCase().includes(s) ||
        link.notes?.toLowerCase()?.includes(s)
    );
  }, [links, debouncedSearch]);

  return (
    <section>
      <SectionHeading>{heading}</SectionHeading>
      <SearchBar value={search} setValue={setSearch} />
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                Name
                <br />
                <Text as="span" $type="extraSmall" $color="gray">
                  Content link
                </Text>
              </Th>
              <Th>Notes</Th>
              <Th>
                <SrOnly>Edit</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            {filteredLinks?.map((link) => (
              <LinksTableRow key={link.id} libraryLink={link} />
            ))}
            <FullWidthRow colSpan={3}>
              <AddButton onClick={() => setShowFormModal(true)}>
                Add new {type}
              </AddButton>
            </FullWidthRow>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <AnimatePresence>
        {showFormModal && (
          <LinkFormModal
            onClose={() => setShowFormModal(false)}
            linkType={type}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default LinksSection;
